import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { filter, finalize } from 'rxjs/operators';
import { ClientOrders, IClientOrders } from 'src/app/models/IAgent.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { AgentsService } from 'src/app/services/agents.service';
import { Log } from 'src/app/services/shared/log';
import { environment } from 'src/environments/environment';

/**
 * ClientList — orders of one client.
 *
 * Route: `/clientorders?token=<client_token>` (standalone only — unlike
 * AgenteList, this view is not embedded anywhere else in the app).
 *
 * The CLIENT_TOKEN is shared across clients (it's an org-level token
 * stored in `environment.CLIENT_TOKEN`), so in practice the route
 * always loads with the same token; we still keep the token-from-param
 * path because the legacy code used it and removing it would change
 * the URL contract for any external link that points here.
 *
 * Renders the order list as a modern card-row table with the same
 * visual language used in AgenteList: row click expands an in-place
 * detail strip, row actions open the preserved child widgets in a
 * side drawer.
 *
 * Architectural notes:
 *  - `OpenModalService.isAgentTrack` / `isAgentOrderB` coordination
 *    replaced by local state (`showTracking`, `showOrderDetails`).
 *  - `IonAccordionGroup` and `Kendo GridComponent` dropped — modern
 *    custom table.
 *  - `markForCheck()` after every async mutation (same CD-quirk
 *    pattern as the rest of this restyle pass).
 */
@Component({
  selector: 'app-client-list',
  templateUrl: './client-list.component.html',
  styleUrl: './client-list.component.scss',
  standalone: false,
})


export class ClientListComponent implements OnInit {

  private readonly agentsService = inject(AgentsService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Token used to load the client orders. Read from the route query
   * param; we keep the `@Input` form for symmetry with AgenteList and
   * for the edge case of someone embedding this view in the future
   * (none today).
   */
  @Input() client_token = '';

  // ── Data ──────────────────────────────────────────────────────────
  orders: ClientOrders[] = [];

  // ── UI state ──────────────────────────────────────────────────────
  searchText = '';
  currentPage = 0;
  static readonly PAGE_SIZE = 30;

  // ── Modal / drawer state ─────────────────────────────────────────
  showTracking = false;
  trackingTrackId = '';

  showOrderDetails = false;
  detailsOrderId: number | string = '';
  /** Human-readable order number ("19359/00") for the modal title.
   *  Separate from `detailsOrderId` which is the numeric primary key
   *  the widget needs for its API calls. */
  detailsOrderNumber = '';
  detailsAssignedDate = '';
  detailsStatusCod = 0;
  detailsTokenAgent = '';
  detailsAgentId: string = '';

  // ── Async flag ────────────────────────────────────────────────────
  loading = false;

  ngOnInit(): void {
    if (this.client_token) {
      // Token already provided by a parent (not used today, kept for
      // future embed scenarios).
      this.loadOrders(this.client_token);
    } 
    else {
      this.route.queryParams
        .pipe(
          filter((params) => !!params['token']),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((params) => {
          this.client_token = params['token'];
          this.loadOrders(this.client_token);
          this.cdr.detectChanges();
        });
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Derived state.
  // ──────────────────────────────────────────────────────────────────

  get filteredOrders(): ClientOrders[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.orders;
    return this.orders.filter((o) => this.haystack(o).includes(q));
  }

  get pagedOrders(): ClientOrders[] {
    const start = this.currentPage * ClientListComponent.PAGE_SIZE;
    return this.filteredOrders.slice(
      start,
      start + ClientListComponent.PAGE_SIZE,
    );
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredOrders.length / ClientListComponent.PAGE_SIZE),
    );
  }

  get hasOrders(): boolean {
    return this.orders.length > 0;
  }

  get partialCount(): number {
    return this.orders.filter((o) => o.or_partial === 'S').length;
  }

  // ──────────────────────────────────────────────────────────────────
  // Template helpers.
  // ──────────────────────────────────────────────────────────────────

  isPartial(order: ClientOrders): boolean {
    return order.or_partial === 'S';
  }

  priorityVariant(order: ClientOrders): 'alta' | 'media' | 'bassa' | 'default' {
    switch (order.tab_order_priority_cod) {
      case 0:
      case 1:
        return 'alta';
      case 2:
        return 'media';
      case 3:
        return 'bassa';
      default:
        return 'default';
    }
  }

  progress(order: ClientOrders): number {
    const raw =
      order.tab_order_status_cod === 100
        ? order.tab_order_status_previous_perc
        : order.tab_order_status_perc;
    const n = Number(raw ?? 0);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
  }

  formatDate(value: string | number | null | undefined): string {
    if (!value) return '—';
    if (String(value).startsWith('0001-01-01')) return '—';
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions.
  // ──────────────────────────────────────────────────────────────────

  onSearchInput(): void {
    this.currentPage = 0;
  }

  clearSearch(): void {
    if (!this.searchText) return;
    this.searchText = '';
    this.currentPage = 0;
  }

  prevPage(): void {
    if (this.currentPage > 0) this.currentPage -= 1;
  }
  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) this.currentPage += 1;
  }

  /**
   * 🚨 FIX v2.11.1 (#13 prod): widget `track_ID` is used as `orderId` on
   * the BE shipment-status endpoint. Passing `os_tracking_number`
   * (carrier code, often empty) made the lookup fail for every client →
   * "Nessuna info disponibile". Now passing `order_id`.
   */
  openTracking(order: ClientOrders): void {
    this.trackingTrackId = String(order.order_id ?? '');
    this.showTracking = true;
  }

  closeTracking(): void {
    this.showTracking = false;
  }

  openOrderDetails(order: ClientOrders): void {
    this.detailsOrderId = order.order_id;
    this.detailsOrderNumber = order.or_order_number ?? '';
    this.detailsAssignedDate = String(order.or_assigned_date ?? '');
    this.detailsStatusCod = order.tab_order_status_cod ?? 0;
    // The order-details widget consumes the client/agent token via the
    // same `token_agent` @Input. Pass the CLIENT_TOKEN through unchanged
    // (HttpClient will URL-encode once on serialization — do NOT
    // pre-encode here, that was the same bug we hit on /agentsorders).
    this.detailsTokenAgent =
      this.client_token || String(environment.CLIENT_TOKEN ?? '');
    this.detailsAgentId = String(order.or_assigned_id ?? '');
    this.showOrderDetails = true;
  }

  closeOrderDetails(): void {
    this.showOrderDetails = false;
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  private loadOrders(token: string): void {
    if (!token) return;
    this.loading = true;
    this.agentsService
      .getCustomersOrders(token)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: unknown) => {
          // getCustomersOrders' return type is loosely typed; accept
          // either an array of rows or an IWsResult-shaped wrapper.
          const data = this.unwrapOrders(result);
          this.orders = data;
          if (this.currentPage >= this.totalPages) {
            this.currentPage = this.totalPages - 1;
          }
          this.cdr.markForCheck();
        },
        error: (err) => Log.error('clients/orders: load failed', err),
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Private helpers.
  // ──────────────────────────────────────────────────────────────────

  /**
   * `AgentsService.getCustomersOrders` doesn't declare a precise type
   * (the service still returns the raw IWsResult or an array depending
   * on the backend payload). Normalise here so the template can rely
   * on `orders: ClientOrders[]`.
   */
  private unwrapOrders(result: unknown): ClientOrders[] {
    if (Array.isArray(result)) {
      return result as ClientOrders[];
    }
    const wrapped = result as IWsResult | null | undefined;
    if (wrapped && Array.isArray(wrapped.data)) {
      return wrapped.data as ClientOrders[];
    }
    return [];
  }

  private haystack(order: ClientOrders): string {
    return [
      order.or_order_number,
      order.or_customer,
      order.or_quote_number,
      order.cl_client,
      String(order.or_client_code ?? ''),
      order.ag_first_name,
      order.ac_first_name,
      order.ac_last_name,
      order.tab_order_status_desc,
      order.tab_order_priority_desc,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  /**
   * Reference to the model class for the template (helps Angular's
   * template type-checker stay happy if we ever enable strict
   * templates). Not actually used at runtime, just keeps the import
   * from being tree-shaken accidentally.
   */
  protected readonly OrderClass = ClientOrders;
  /** Reference to keep the interface import non-dead. */
  protected readonly OrderInterface!: IClientOrders;
  
}
